import { observable, flow, makeObservable } from 'mobx';
import groupBy from 'lodash/groupBy';
import each from 'lodash/each';
import set from 'lodash/set';
import includes from 'lodash/includes';
import map from 'lodash/map';

import BaseStore from './BaseStore';
import MeetingsAPI from '../api/meetings';

class MeetingStore extends BaseStore {
  isBusy = false;
  isSending = false;
  meetingSlots = {};
  days = [];
  meetingSubjects = [];
  isMeetingSubjectsLoading = false;
  meetingsCount = 0;
  isMeetingsCountLoading = false;
  isAlreadyBooked = false;
  meetingSessions = {};

  getMeetingSessions = flow(function* (
    schoolId,
    minDate,
    maxDate,
    callback,
    withSlotsFormat
  ) {
    this.isBusy = true;
    try {
      const { data } = yield MeetingsAPI.list(schoolId, minDate, maxDate);

      this.setMeetingSlots(data, callback);

      if (withSlotsFormat) {
        this.setMeetingSessions(data);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error.response.data.errorMessages);
    } finally {
      this.isBusy = false;
    }
  });

  getMeetingSubjects = flow(function* (schoolId) {
    try {
      this.isMeetingSubjectsLoading = true;
      const { data } = yield MeetingsAPI.listMeetingSubjects(schoolId);

      this.setMeetingSubjects(data);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error.response.data.errorMessages);
    } finally {
      this.isMeetingSubjectsLoading = false;
    }
  });

  getMeetingsCount = flow(function* (schoolId) {
    try {
      this.isMeetingsCountLoading = true;
      const { data } = yield MeetingsAPI.count(schoolId);
      this.meetingsCount = data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error.response.data.errorMessages);
    } finally {
      this.isMeetingsCountLoading = false;
    }
  });

  bookMeeting = flow(function* (payload, callbackObj) {
    const { setErrors } = callbackObj;

    this.isAlreadyBooked = false;
    this.isSending = true;
    try {
      yield MeetingsAPI.book(payload);
    } catch (error) {
      const { errorMessages } = error.response.data;
      setErrors(errorMessages);
      if (includes(errorMessages.scheduled_at, 'Already booked')) {
        this.isAlreadyBooked = true;
      }
    } finally {
      this.isSending = false;
    }
  });

  setMeetingSlots = (data, callback) => {
    let currentDate;

    const groupedArray = groupBy(data, (slot) => {
      [currentDate] = slot.split('T');

      return currentDate;
    });

    this.days = Object.keys(groupedArray);
    this.meetingSlots = groupedArray;
    callback(this.days[0]);
  };

  setMeetingSubjects = (data) => {
    this.meetingSubjects = each(data, (category) => {
      set(category, 'id', category.name);
    });
  };

  setMeetingSessions = (data) => {
      let currentDate;

      const mapDataToSlotsFormat = map(data, (slot) => (
        {
          start_date: slot,
        }
      ));
  
      this.meetingSessions = groupBy(mapDataToSlotsFormat, (slot) => {
        [currentDate] = slot.start_date.split('T');
  
        return currentDate;
      });
    };

  constructor() {
    super();

    makeObservable(this, {
      isBusy: observable,
      isSending: observable,
      meetingSlots: observable,
      days: observable,
      meetingSubjects: observable,
      isMeetingSubjectsLoading: observable,
      meetingsCount: observable,
      isMeetingsCountLoading: observable,
      isAlreadyBooked: observable,
      meetingSessions: observable,
    });
  }
}

export default new MeetingStore();
